
/* eslint-disable  jsx-a11y/label-has-associated-control  */

import React, { useRef, useState } from "react";
import Select from "react-select";
import "./FilterSection.css";
import { marketTypes, statusArray} from "pagesNewUi/Innovate/InnovateUtils";
import {
  MAX_DATE_RANGE_MONTHS,
  MIN_REPORT_DATE,
  MIN_REPORT_DATE_LABEL,
  getEndDateAfterStartChange,
  getEnteredDatesError,
  getMaxEndDate,
  getTodayDate,
  isValidDateValue,
  validateDateRange
} from "./reportDateUtils";

const MAX_MARKETS_WITHOUT_DATES = 10;

const marketOptions = [
  { value: "all", label: "Select All" },
  ...marketTypes.map(market => ({
    value: market.id,
    label: market.MarketName
  }))
];

const statusOptions = [
  { value: "all", label: "Select All" },
  ...statusArray.map(status => ({
    value: status.value,
    label:
      status.label.charAt(0).toUpperCase() +
      status.label.slice(1).toLowerCase()
  }))
];

const INITIAL_FILTERS = {
  markets: [],
  statuses: [],
  startDate: "",
  endDate: ""
};

const customSelectStyles = {
  control: provided => ({
    ...provided,
    minHeight: "48px",
    borderRadius: "12px",
    borderColor: "#dbe3ef",
    boxShadow: "none",
    "&:hover": {
      borderColor: "#2563eb"
    }
  }),
  menu: provided => ({
    ...provided,
    borderRadius: "12px",
    overflow: "hidden",
    zIndex: 99999
  }),
  menuPortal: provided => ({
    ...provided,
    zIndex: 99999
  }),
  multiValue: provided => ({
    ...provided,
    borderRadius: "8px",
    backgroundColor: "#e0e7ff"
  }),
  multiValueLabel: provided => ({
    ...provided,
    color: "#4338ca",
    fontWeight: 600
  })
};

const FilterSection = ({ onApply, isLoading }) => {
  const [filters, setFilters] = useState(INITIAL_FILTERS);

  const [error, setError] = useState("");

  /*
   * Name of the date input currently being edited.
   * Date errors are hidden while the user is typing.
   */
  const [activeDateField, setActiveDateField] = useState("");

  const startDateOnFocusRef = useRef("");

  const today = getTodayDate();

  const isDateMandatory =
    filters.markets.length > MAX_MARKETS_WITHOUT_DATES;

  const hasIncompleteDateRange =
    Boolean(filters.startDate) !== Boolean(filters.endDate);

  const isStartDateValid = isValidDateValue(
    filters.startDate,
    today
  );

  const maximumEndDate = getMaxEndDate(filters.startDate, today);

  const dateRangeError = validateDateRange(
    filters.startDate,
    filters.endDate,
    {
      isDateRequired: isDateMandatory,
      requiredMessage:
        "Please choose Start Date and End Date when more than 10 markets are selected.",
      todayDate: today
    }
  );

  /*
   * Errors for values the user has actually entered
   * (not for missing values), shown once editing ends.
   */
  const enteredDatesError = getEnteredDatesError(
    filters.startDate,
    filters.endDate,
    today
  );

    const handleMultiSelect = (
        selectedOptions,
        options,
        field
    ) => {
        setError("");
        if (!selectedOptions) {
            setFilters(prev => ({
                ...prev,
                [field]: []
            }));
            return;
        }
        const hasSelectAll = selectedOptions.some(
            item => item.value === "all"
        );
        setFilters(prev => ({
            ...prev,
            [field]: hasSelectAll
                ? options.filter(option => option.value !== "all")
                : selectedOptions
        }));
    };

  /**
   * Store exactly what the native date input reports.
   *
   * While typing, the input can report "" or partial
   * years such as "0002-05-12". Those values are never
   * rewritten here, because writing a different value
   * back into a controlled date input resets what the
   * user is typing. Validation is derived from state.
   */
  const handleDateChange = event => {
    const { name, value } = event.target;

    setError("");

    setFilters(previousFilters =>
      previousFilters[name] === value
        ? previousFilters
        : {
            ...previousFilters,
            [name]: value
          }
    );
  };

  const handleDateFocus = event => {
    const { name } = event.target;

    setActiveDateField(name);

    if (name === "startDate") {
      startDateOnFocusRef.current = filters.startDate;
    }
  };

  /**
   * When Start Date editing is finished, clear End Date
   * only if the new Start Date makes it invalid.
   */
  const handleDateBlur = event => {
    const { name } = event.target;

    setActiveDateField("");

    if (name !== "startDate") {
      return;
    }

    const startDateBeforeEdit = startDateOnFocusRef.current;

    setFilters(previousFilters => {
      const nextEndDate = getEndDateAfterStartChange(
        startDateBeforeEdit,
        previousFilters.startDate,
        previousFilters.endDate,
        today
      );

      return nextEndDate === previousFilters.endDate
        ? previousFilters
        : { ...previousFilters, endDate: nextEndDate };
    });
  };

  const handleApplyFilters = () => {
    setError("");

    const {
      startDate,
      endDate,
      markets,
      statuses
    } = filters;

    /*
     * Final validation, using a fresh "today".
     */
    const validationError = validateDateRange(
      startDate,
      endDate,
      {
        isDateRequired:
          markets.length > MAX_MARKETS_WITHOUT_DATES,
        requiredMessage:
          "Please choose Start Date and End Date when more than 10 markets are selected.",
        todayDate: getTodayDate()
      }
    );

    if (validationError) {
      setError(validationError);
      return;
    }

    const payload = {};

    if (markets.length) {
      payload.market = markets.map(item => item.value);
    }

    if (statuses.length) {
      payload.ideaStatus = statuses.map(
        item => item.value
      );
    }

    if (startDate && endDate) {
      payload.startDate = startDate;
      payload.endDate = endDate;
    }

    onApply?.(payload);
  };

  const handleResetFilters = () => {
    setFilters(INITIAL_FILTERS);
    setError("");
  };

  const hasNoFilters =
    !filters.markets.length &&
    !filters.statuses.length &&
    !filters.startDate &&
    !filters.endDate;

  const isDownloadDisabled =
    hasNoFilters || Boolean(dateRangeError);

  const displayedError =
    error || (activeDateField ? "" : enteredDatesError);

  return (
    <div className="vf-filter-card">
      <div className="vf-filter-header">
        <div>
          <h2 className="vf-filter-title">
            Idea Report Filters
          </h2>

          <p className="vf-filter-subtitle">
            Filter ideas using market, status and date range
          </p>
        </div>
      </div>

      <div className="vf-filter-grid">
        <div className="vf-filter-item">
          <label
            htmlFor="idea-report-market"
            className="vf-filter-label"
          >
            Market
          </label>

          <Select
            inputId="idea-report-market"
            isMulti
            closeMenuOnSelect={false}
            options={marketOptions}
            value={filters.markets}
            styles={customSelectStyles}
            classNamePrefix="vf-select"
            placeholder="Select Market(s)"
            onChange={value =>
              handleMultiSelect(
                value,
                marketOptions,
                "markets"
              )
            }
          />
        </div>

        <div className="vf-filter-item">
          <label
            htmlFor="idea-report-status"
            className="vf-filter-label"
          >
            Idea Status
          </label>

          <Select
            inputId="idea-report-status"
            isMulti
            closeMenuOnSelect={false}
            options={statusOptions}
            value={filters.statuses}
            styles={customSelectStyles}
            classNamePrefix="vf-select"
            placeholder="Select Status(es)"
            onChange={value =>
              handleMultiSelect(
                value,
                statusOptions,
                "statuses"
              )
            }
          />
        </div>

        <div className="vf-filter-item">
          <label
            htmlFor="idea-report-start-date"
            className="vf-filter-label"
          >
            Start Date

            {(isDateMandatory || filters.endDate) && (
              <span
                className="vf-required"
                aria-hidden="true"
                style={{ color: "red" }}
              >
                {" "}
                *
              </span>
            )}

            <span className="icon-info tooltip_v">
              <span className="tooltiptext_v">
                Select a date range for idea refinement
                from {MIN_REPORT_DATE_LABEL} onwards
                (maximum {MAX_DATE_RANGE_MONTHS} months)
              </span>
            </span>
          </label>

          <input
            id="idea-report-start-date"
            type="date"
            name="startDate"
            value={filters.startDate}
            min={MIN_REPORT_DATE}
            max={today}
            className="vf-filter-input"
            aria-required={
              isDateMandatory || Boolean(filters.endDate)
            }
            onChange={handleDateChange}
            onFocus={handleDateFocus}
            onBlur={handleDateBlur}
          />
        </div>

        <div className="vf-filter-item">
          <label
            htmlFor="idea-report-end-date"
            className="vf-filter-label"
          >
            End Date

            {(isDateMandatory || filters.startDate) && (
              <span
                className="vf-required"
                aria-hidden="true"
                style={{ color: "red" }}
              >
                {" "}
                *
              </span>
            )}

            <span className="icon-info tooltip_v">
              <span className="tooltiptext_v">
                Select a date range for idea refinement
                from {MIN_REPORT_DATE_LABEL} onwards
                (maximum {MAX_DATE_RANGE_MONTHS} months)
              </span>
            </span>
          </label>

          <input
            id="idea-report-end-date"
            type="date"
            name="endDate"
            value={filters.endDate}
            min={
              isStartDateValid
                ? filters.startDate
                : MIN_REPORT_DATE
            }
            max={maximumEndDate}
            disabled={!isStartDateValid}
            className="vf-filter-input"
            aria-required={
              isDateMandatory || Boolean(filters.startDate)
            }
            onChange={handleDateChange}
            onFocus={handleDateFocus}
            onBlur={handleDateBlur}
          />
        </div>
      </div>

      {isDateMandatory && (
        <p className="vf-challenge-filter-error">
          More than 10 markets selected. Please select
          Start Date and End Date. The date range must be
          within {MAX_DATE_RANGE_MONTHS} months.
        </p>
      )}

      {!isDateMandatory &&
        hasIncompleteDateRange &&
        !activeDateField && (
          <p className="vf-challenge-filter-error">
            Please select both Start Date and End Date.
          </p>
        )}

      {displayedError && (
        <div
          className="vf-filter-error"
          role="alert"
          aria-live="assertive"
        >
          {displayedError}
        </div>
      )}

      <div className="vf-filter-actions">
        <button
          type="button"
          className="vf-btn1 vf-btn-primaryReport"
          onClick={handleApplyFilters}
          disabled={isLoading || isDownloadDisabled}
        >
          {isLoading
            ? "Downloading..."
            : "Download Idea Reports"}
        </button>

        <button
          type="button"
          className="vf-btn1 vf-btn-secondaryReport"
          disabled={isLoading}
          onClick={handleResetFilters}
        >
          Reset
        </button>
      </div>
    </div>
  );
};

export default FilterSection;
